import React, { useState, useMemo } from 'react';
import { Button, Modal, Form, Alert, Spinner, Table, Badge } from 'react-bootstrap';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { useNotificationRules } from '../../hooks/notifications/useNotificationRules';
import { useUsersData } from '../../Helpers/UsersContext';
import { useBuildingsData } from '../../Helpers/BuildingSummariesContext';
import { NotificationRuleWithUser } from '../../types/notifications';
import { translate } from '../../types/EnumTranslations';
import { useBuildingTypeFilterTypes } from '../../hooks/useEnabledBuildingTypes';
import { offeredBuildingTypes } from '../../Helpers/buildingTypeOptions';
import './NotificationRulesPage.css';

const NotificationRulesPage: React.FC = () => {
    const { rules, loading, error, submitting, createRule, updateRule, deleteRule, clearError } = useNotificationRules();
    const { users, loadingUsers } = useUsersData();
    const { buildings, loadingSummaries } = useBuildingsData();

    // Modal states
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [editingRule, setEditingRule] = useState<NotificationRuleWithUser | null>(null);
    const [deletingRule, setDeletingRule] = useState<NotificationRuleWithUser | null>(null);

    // Form state
    const [selectedUserId, setSelectedUserId] = useState('');
    const [selectedBuildingTypes, setSelectedBuildingTypes] = useState<string[]>([]);

    // Enabled types plus those a building still has, plus any the edited rule already holds
    const typesInUse = useMemo(() => (buildings ?? []).map(building => building.buildingType), [buildings]);
    const filterTypes = useBuildingTypeFilterTypes(typesInUse);
    const buildingTypeChoices = useMemo(
        () => offeredBuildingTypes(filterTypes, selectedBuildingTypes),
        [filterTypes, selectedBuildingTypes],
    );
    const [selectedBuildings, setSelectedBuildings] = useState<string[]>([]);
    const [formError, setFormError] = useState<string | null>(null);

    // Get admin users for selection
    const adminUsers = useMemo(() => {
        if (!users) return [];
        return users.filter(u => (u.admin || u.superadmin) && !u.deleted);
    }, [users]);

    // Building options for selection
    const buildingOptions = useMemo(() => {
        if (!buildings) return [];
        return buildings
            .map(b => ({ key: b.key, name: b.name || b.key }))
            .sort((a, b) => a.name.localeCompare(b.name, 'sv'));
    }, [buildings]);

    // Reset form
    const resetForm = () => {
        setSelectedUserId('');
        setSelectedBuildingTypes([]);
        setSelectedBuildings([]);
        setFormError(null);
    };

    // Open add modal
    const handleOpenAddModal = () => {
        resetForm();
        setShowAddModal(true);
    };

    // Open edit modal
    const handleOpenEditModal = (rule: NotificationRuleWithUser) => {
        setEditingRule(rule);
        setSelectedBuildingTypes([...rule.buildingTypes]);
        setSelectedBuildings([...rule.buildings]);
        setFormError(null);
        setShowEditModal(true);
    };

    // Open delete confirmation
    const handleOpenDeleteModal = (rule: NotificationRuleWithUser) => {
        setDeletingRule(rule);
        setShowDeleteModal(true);
    };

    // Handle add submit
    const handleAddSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormError(null);

        if (!selectedUserId) {
            setFormError('Välj en användare');
            return;
        }

        if (selectedBuildingTypes.length === 0 && selectedBuildings.length === 0) {
            setFormError('Välj minst en byggnadstyp eller byggnad');
            return;
        }

        try {
            await createRule(selectedUserId, selectedBuildingTypes, selectedBuildings);
            setShowAddModal(false);
            resetForm();
        } catch {
            // Error is handled by the hook
        }
    };

    // Handle edit submit
    const handleEditSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormError(null);

        if (!editingRule) return;

        if (selectedBuildingTypes.length === 0 && selectedBuildings.length === 0) {
            setFormError('Välj minst en byggnadstyp eller byggnad');
            return;
        }

        try {
            await updateRule(editingRule.userId, selectedBuildingTypes, selectedBuildings);
            setShowEditModal(false);
            setEditingRule(null);
            resetForm();
        } catch {
            // Error is handled by the hook
        }
    };

    // Handle delete
    const handleDelete = async () => {
        if (!deletingRule) return;

        try {
            await deleteRule(deletingRule.userId);
            setShowDeleteModal(false);
            setDeletingRule(null);
        } catch {
            // Error is handled by the hook
        }
    };

    // Toggle building type selection
    const toggleBuildingType = (type: string) => {
        setSelectedBuildingTypes(prev =>
            prev.includes(type)
                ? prev.filter(t => t !== type)
                : [...prev, type]
        );
    };

    // Toggle building selection
    const toggleBuilding = (buildingKey: string) => {
        setSelectedBuildings(prev =>
            prev.includes(buildingKey)
                ? prev.filter(k => k !== buildingKey)
                : [...prev, buildingKey]
        );
    };

    // Format building types for display
    const formatBuildingTypes = (types: string[]): string => {
        if (types.length === 0) return '-';
        return types.map(t => translate(t)).join(', ');
    };

    // Format buildings for display
    const formatBuildings = (buildingKeys: string[]): string => {
        if (buildingKeys.length === 0) return '-';
        return buildingKeys
            .map(key => {
                const building = buildings?.find(b => b.key === key);
                return building?.name || key;
            })
            .join(', ');
    };

    // Loading state
    if (loading || loadingUsers || loadingSummaries) {
        return (
            <div className="notification-rules-loading">
                <Spinner animation="border" />
                <span>Laddar aviseringsregler...</span>
            </div>
        );
    }

    return (
        <div className="notification-rules-page">
            {error && (
                <Alert variant="danger" dismissible onClose={clearError}>
                    {error}
                </Alert>
            )}

            <div className="notification-rules-header">
                <div>
                    <p className="notification-rules-description">
                        Konfigurera vilka administratörer som ska få e-postaviseringar
                        när nya felanmälningar skapas för specifika byggnadstyper eller byggnader.
                    </p>
                </div>
                <Button variant="primary" onClick={handleOpenAddModal}>
                    <Plus size={18} /> Lägg till regel
                </Button>
            </div>

            {rules.length === 0 ? (
                <div className="notification-rules-empty">
                    <p>Inga aviseringsregler har konfigurerats ännu.</p>
                    <p>Klicka på "Lägg till regel" för att skapa den första regeln.</p>
                </div>
            ) : (
                <Table className="notification-rules-table">
                    <thead>
                        <tr>
                            <th>Användare</th>
                            <th>Byggnadstyper</th>
                            <th>Specifika byggnader</th>
                            <th style={{ width: 100 }}>Åtgärder</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rules.map(rule => (
                            <tr key={rule.userId}>
                                <td className="notification-rule-user">{rule.userName}</td>
                                <td className="notification-rule-types">
                                    {rule.buildingTypes.length > 0 ? (
                                        rule.buildingTypes.map(type => (
                                            <Badge key={type} bg="secondary" className="me-1">
                                                {translate(type)}
                                            </Badge>
                                        ))
                                    ) : (
                                        <span className="text-muted">-</span>
                                    )}
                                </td>
                                <td className="notification-rule-buildings">
                                    {rule.buildings.length > 0 ? (
                                        <span title={formatBuildings(rule.buildings)}>
                                            {rule.buildings.length} {rule.buildings.length === 1 ? 'byggnad' : 'byggnader'}
                                        </span>
                                    ) : (
                                        <span className="text-muted">-</span>
                                    )}
                                </td>
                                <td className="notification-rule-actions">
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={() => handleOpenEditModal(rule)}
                                        title="Redigera"
                                        style={{ color: '#001D60' }}
                                    >
                                        <Pencil size={16} />
                                    </Button>
                                    <Button
                                        variant="link"
                                        size="sm"
                                        className="text-danger"
                                        onClick={() => handleOpenDeleteModal(rule)}
                                        title="Ta bort"
                                    >
                                        <Trash2 size={16} />
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            {/* Add Rule Modal */}
            <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Lägg till aviseringsregel</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleAddSubmit}>
                    <Modal.Body>
                        {(formError || error) && (
                            <Alert variant="danger">{formError || error}</Alert>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label>Användare</Form.Label>
                            <Form.Select
                                value={selectedUserId}
                                onChange={e => setSelectedUserId(e.target.value)}
                            >
                                <option value="">Välj användare...</option>
                                {adminUsers.map(user => (
                                    <option key={user.key} value={user.key}>
                                        {user.name}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Text className="text-muted">
                                Endast administratörer kan väljas för aviseringar.
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Byggnadstyper</Form.Label>
                            <div className="building-type-checkboxes">
                                {buildingTypeChoices.map(type => (
                                    <Form.Check
                                        key={type}
                                        type="checkbox"
                                        id={`add-type-${type}`}
                                        label={translate(type)}
                                        checked={selectedBuildingTypes.includes(type)}
                                        onChange={() => toggleBuildingType(type)}
                                    />
                                ))}
                            </div>
                            <Form.Text className="text-muted">
                                Användaren aviseras för alla felanmälningar på byggnader av dessa typer.
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Specifika byggnader</Form.Label>
                            <div className="building-checkboxes">
                                {buildingOptions.map(building => (
                                    <Form.Check
                                        key={building.key}
                                        type="checkbox"
                                        id={`add-building-${building.key}`}
                                        label={building.name}
                                        checked={selectedBuildings.includes(building.key)}
                                        onChange={() => toggleBuilding(building.key)}
                                    />
                                ))}
                            </div>
                            <Form.Text className="text-muted">
                                Användaren aviseras för felanmälningar på dessa specifika byggnader.
                            </Form.Text>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowAddModal(false)}>
                            Avbryt
                        </Button>
                        <Button variant="primary" type="submit" disabled={submitting}>
                            {submitting ? <Spinner size="sm" /> : 'Spara'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Edit Rule Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Redigera aviseringsregel</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleEditSubmit}>
                    <Modal.Body>
                        {(formError || error) && (
                            <Alert variant="danger">{formError || error}</Alert>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label>Användare</Form.Label>
                            <Form.Control
                                type="text"
                                value={editingRule?.userName || ''}
                                disabled
                            />
                            <Form.Text className="text-muted">
                                Användaren kan inte ändras. Ta bort regeln och skapa en ny för annan användare.
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Byggnadstyper</Form.Label>
                            <div className="building-type-checkboxes">
                                {buildingTypeChoices.map(type => (
                                    <Form.Check
                                        key={type}
                                        type="checkbox"
                                        id={`edit-type-${type}`}
                                        label={translate(type)}
                                        checked={selectedBuildingTypes.includes(type)}
                                        onChange={() => toggleBuildingType(type)}
                                    />
                                ))}
                            </div>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Specifika byggnader</Form.Label>
                            <div className="building-checkboxes">
                                {buildingOptions.map(building => (
                                    <Form.Check
                                        key={building.key}
                                        type="checkbox"
                                        id={`edit-building-${building.key}`}
                                        label={building.name}
                                        checked={selectedBuildings.includes(building.key)}
                                        onChange={() => toggleBuilding(building.key)}
                                    />
                                ))}
                            </div>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                            Avbryt
                        </Button>
                        <Button variant="primary" type="submit" disabled={submitting}>
                            {submitting ? <Spinner size="sm" /> : 'Spara'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Ta bort aviseringsregel</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    <p>
                        Är du säker på att du vill ta bort aviseringsregeln för{' '}
                        <strong>{deletingRule?.userName}</strong>?
                    </p>
                    <p className="text-muted">
                        Användaren kommer inte längre få e-postaviseringar för felanmälningar.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Avbryt
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={submitting}>
                        {submitting ? <Spinner size="sm" /> : 'Ta bort'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default NotificationRulesPage;
